import { Component, OnInit, inject } from "@angular/core";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { PeriodService } from "./shared/period.service";
import { ModuleType } from "@enums/ModuleType";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { ToastrService } from "ngx-toastr";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { firstValueFrom } from "rxjs";
import { TranslocoPipe, TranslocoService, provideTranslocoScope } from "@jsverse/transloco";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { GenericListManagerComponent } from "@app/shared/generic-managers/generic-list-manager/generic-list-manager.component";

@Component({
    selector: "app-periods",
    templateUrl: "./periods.component.html",
    styleUrls: ["./periods.component.scss"],
    imports: [TranslocoPipe, MenuSystemDirective, GenericListManagerComponent],
    providers: [provideTranslocoScope("periods")],
})
export class PeriodsComponent implements OnInit {
    private _ds = inject(PeriodService);
    private _toastr = inject(ToastrService);
    private _promptService = inject(CerrixPromptService);
    private _transloco = inject(TranslocoService);

    tab: TabModel;

    selectedModule = ModuleType.ORM;
    moduleTypes = ModuleType;

    periods: any[];

    configLoaded = false;
    config: GenericListConfig;

    isRiskAdminU: boolean;
    isBimAdminU: boolean;

    async ngOnInit() {
        await firstValueFrom(this._transloco.load(`periods/${this._transloco.getActiveLang()}`));

        this._ds.getPermissions().subscribe((perm) => {
            this.isBimAdminU = perm.isBimAdminU;
            this.isRiskAdminU = perm.isRiskAdminU;

            if (!this.isRiskAdminU) {
                // make sure you see the BIM data, when you heve no Risk rights.
                this.selectedModule = ModuleType.BIM;
            }
            this.loadConfig();
        });
    }

    tabChanged(newTab: TabMenuItem) {
        this.selectedModule = +newTab.menuItemId;
        this.loadConfig();
    }

    loadConfig() {
        this.configLoaded = false;

        const moduleType = ModuleType[this.selectedModule];
        const t = (key: string, params?: object) => this._transloco.translate(`periods.${key}`, params);

        this.config = <GenericListConfig>{
            name: t("listName", { moduleType }),
            uiid: `tbl-period-list-${moduleType}`,
            dataMethod: () => {
                return this._ds.getByModule(this.selectedModule);
            },

            allowAdd: true,
            allowEdit: true,

            uiidAdd: "btn-add-period",
            uiidReload: "btn-reload-period",

            fields: [
                {
                    fieldName: "ID",
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                    hideInEdit: true,
                },
                {
                    fieldName: "PeriodName",
                    prettyName: t("colPeriodName"),
                    fieldType: GenericListFieldType.Text,
                    required: true,
                    uiid: "period-name-input",
                },
                {
                    fieldName: "Active",
                    prettyName: t("colActive"),
                    fieldType: GenericListFieldType.CheckBox,
                    hideInEdit: true,
                },
                {
                    fieldName: "StartDate",
                    prettyName: t("colStartDate"),
                    fieldType: GenericListFieldType.Date,
                    uiid: "period-start-input",
                },
                {
                    fieldName: "EndDate",
                    prettyName: t("colEndDate"),
                    fieldType: GenericListFieldType.Date,
                    uiid: "period-end-input",
                },
                {
                    fieldName: "PeriodClosed",
                    prettyName: t("colPeriodClosed"),
                    fieldType: GenericListFieldType.Date,
                    isReadonly: true,
                    hideInEdit: true,
                },
                {
                    fieldName: "AuthUserName",
                    prettyName: t("colStartedBy"),
                    fieldType: GenericListFieldType.Text,
                    hideInEdit: true,
                },
            ],

            add: async (row) => {
                // Creates a random 4 digit number within the 0000 - 9999 range
                const confirmationNumber = (Math.floor(Math.random() * 10000) + 10000)
                    .toString()
                    .substring(1);

                const enteredNumber = (
                    await this._promptService
                        .prompt({
                            maxHeight: "375px",
                            maxWidth: "440px",
                            data: {
                                title: t("createPeriodTitle"),
                                message: t("createConfirmMessage", { moduleType, confirmationNumber }),
                                fields: [
                                    {
                                        prettyName: t("confirmationNumberLabel"),
                                        fieldName: "confirmationnumber",
                                        fieldType: GenericListFieldType.Text,
                                        uiid: "period-confirmation-number-input",
                                    },
                                ],
                                centerText: true,
                            },
                        })
                        .toPromise()
                ).confirmationnumber;

                if (confirmationNumber === enteredNumber) {
                    await firstValueFrom(this._ds.store(this.selectedModule, row));
                    this._toastr.success(t("createSuccessMessage"), t("createSuccessTitle"));
                    return true;
                } else if (enteredNumber && enteredNumber.length > 0) {
                    this._toastr.warning(t("confirmationMismatchMessage"), t("confirmationMismatchTitle"));
                }

                return false;
            },
            edit: async (row) => {
                await firstValueFrom(this._ds.store(this.selectedModule, row));
                this._toastr.success(t("updateSuccessMessage"), t("updateSuccessTitle"));
                return true;
            },
        };

        this.configLoaded = true;
    }
}
